import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import {
    FiRefreshCw, FiWifi, FiWifiOff, FiDatabase, FiUsers,
    FiAlertTriangle, FiCheck, FiTrash2, FiClock, FiHardDrive,
    FiImage, FiFile
} from 'react-icons/fi';

const isDev_API = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API = isDev_API ? 'http://localhost:5056/api' : '/api';
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('sp_token')}`, 'Content-Type': 'application/json' });

const Synchronisation: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<'overview' | 'commerciaux' | 'conflicts' | 'connexions'>('overview');
    const [forceLoading, setForceLoading] = useState(false);

    useEffect(() => { fetchStatus(); }, []);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const resp = await fetch(`${API}/sync/status`, { headers: getHeaders() });
            const json = await resp.json();
            setData(json);
        } catch { setData(null); }
        setLoading(false);
    };

    const handleForceSync = async () => {
        if (!window.confirm('Forcer la synchronisation de toutes les activites en attente ?')) return;
        setForceLoading(true);
        try {
            await fetch(`${API}/sync/force`, { method: 'POST', headers: getHeaders() });
            fetchStatus();
        } catch { alert('Erreur'); }
        setForceLoading(false);
    };

    const handleResolveConflict = async (keepId: number, deleteId: number) => {
        if (!window.confirm(`Garder #${keepId} et supprimer #${deleteId} ?`)) return;
        try {
            await fetch(`${API}/sync/resolve-conflict`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ keep_id: keepId, delete_id: deleteId })
            });
            fetchStatus();
        } catch { alert('Erreur'); }
    };

    if (loading) return <div className="loading">Chargement...</div>;
    if (!data) return <div className="error">Erreur de chargement</div>;

    const r = data.resume || {};
    const syncPercent = r.taux_sync || 0;

    // Timeline chart
    const timelineOpts: ApexCharts.ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif', stacked: true },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        colors: ['#16A34A', '#F59E0B'],
        xaxis: { categories: data.timeline?.map((t: any) => new Date(t.jour).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' })) || [] },
        legend: { position: 'top' },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
        dataLabels: { enabled: false },
    };

    const timelineSeries = [
        { name: 'Synchronisees', data: data.timeline?.map((t: any) => t.synced || 0) || [] },
        { name: 'En attente', data: data.timeline?.map((t: any) => (t.total || 0) - (t.synced || 0)) || [] },
    ];

    return (
        <div className="page-sync">
            <div className="page-header">
                <h2><FiRefreshCw /> Synchronisation & Performance</h2>
                <div className="header-actions">
                    <button className="btn btn-secondary" onClick={fetchStatus}><FiRefreshCw /> Actualiser</button>
                    <button className="btn btn-primary" onClick={handleForceSync} disabled={forceLoading}>
                        <FiWifi /> {forceLoading ? 'Sync...' : 'Forcer sync'}
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
                <div className="kpi-card" style={{ borderLeftColor: '#16A34A' }}>
                    <div className="kpi-content">
                        <span className="kpi-value" style={{ color: syncPercent >= 90 ? '#16A34A' : syncPercent >= 70 ? '#F59E0B' : '#EF4444' }}>
                            {syncPercent}%
                        </span>
                        <span className="kpi-label">Taux de synchronisation</span>
                    </div>
                </div>
                <div className="kpi-card" style={{ borderLeftColor: '#3B82F6' }}>
                    <div className="kpi-content">
                        <span className="kpi-value">{(r.total_activites || 0).toLocaleString()}</span>
                        <span className="kpi-label">Total activites</span>
                    </div>
                </div>
                <div className="kpi-card" style={{ borderLeftColor: '#F59E0B' }}>
                    <div className="kpi-content">
                        <span className="kpi-value">{r.non_synced || 0}</span>
                        <span className="kpi-label">En attente de sync</span>
                    </div>
                </div>
                <div className="kpi-card" style={{ borderLeftColor: '#EF4444' }}>
                    <div className="kpi-content">
                        <span className="kpi-value">{data.conflicts?.length || 0}</span>
                        <span className="kpi-label">Conflits detectes</span>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs" style={{ marginBottom: 16 }}>
                <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
                    <FiDatabase size={14} /> Vue d'ensemble
                </button>
                <button className={`tab ${tab === 'commerciaux' ? 'active' : ''}`} onClick={() => setTab('commerciaux')}>
                    <FiUsers size={14} /> Par commercial
                </button>
                <button className={`tab ${tab === 'conflicts' ? 'active' : ''}`} onClick={() => setTab('conflicts')}>
                    <FiAlertTriangle size={14} /> Conflits ({data.conflicts?.length || 0})
                </button>
                <button className={`tab ${tab === 'connexions' ? 'active' : ''}`} onClick={() => setTab('connexions')}>
                    <FiClock size={14} /> Connexions
                </button>
            </div>

            {/* Overview */}
            {tab === 'overview' && (
                <div className="charts-grid">
                    <div className="chart-card">
                        <h3>Synchronisation (7 derniers jours)</h3>
                        <Chart options={timelineOpts} series={timelineSeries} type="bar" height={300} />
                    </div>
                    <div className="chart-card">
                        <h3><FiHardDrive size={16} /> Volume de donnees</h3>
                        <div style={{ padding: 20 }}>
                            <div className="volume-stats">
                                {[
                                    { label: 'Activites', value: data.volume?.total_activites || 0, icon: <FiDatabase size={16} />, color: '#16A34A' },
                                    { label: 'Clients', value: data.volume?.total_clients || 0, icon: <FiUsers size={16} />, color: '#3B82F6' },
                                    { label: 'Commandes', value: data.volume?.total_commandes || 0, icon: <FiFile size={16} />, color: '#8B5CF6' },
                                    { label: 'Photos', value: data.volume?.total_photos || 0, icon: <FiImage size={16} />, color: '#F59E0B' },
                                    { label: 'Documents', value: data.volume?.total_documents || 0, icon: <FiFile size={16} />, color: '#EF4444' },
                                ].map((v, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666' }}>
                                            <span style={{ color: v.color }}>{v.icon}</span> {v.label}
                                        </span>
                                        <strong>{v.value.toLocaleString()}</strong>
                                    </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 20, padding: 12, background: '#f0fdf4', borderRadius: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                    <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 600 }}>Aujourd'hui</span>
                                </div>
                                <span style={{ fontSize: 12, color: '#666' }}>
                                    {data.today?.activites_today || 0} activites dont {data.today?.synced_today || 0} synchronisees
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Par commercial */}
            {tab === 'commerciaux' && (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Commercial</th>
                                <th>Matricule</th>
                                <th>Activites (30j)</th>
                                <th>Synchronisees</th>
                                <th>En attente</th>
                                <th>Derniere activite</th>
                                <th>Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.par_commercial || []).map((c: any, i: number) => {
                                const syncRate = c.total_activites > 0 ? Math.round((c.synced / c.total_activites) * 100) : 100;
                                return (
                                    <tr key={i}>
                                        <td><strong>{c.commercial}</strong></td>
                                        <td>{c.matricule || '--'}</td>
                                        <td>{c.total_activites || 0}</td>
                                        <td style={{ color: '#16A34A' }}>{c.synced || 0}</td>
                                        <td>
                                            {c.en_attente > 0 ? (
                                                <span style={{ color: '#F59E0B', fontWeight: 600 }}>{c.en_attente}</span>
                                            ) : (
                                                <span style={{ color: '#16A34A' }}><FiCheck size={14} /></span>
                                            )}
                                        </td>
                                        <td>{c.derniere_activite ? new Date(c.derniere_activite).toLocaleDateString('fr-FR') : '--'}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <div style={{ width: 60, height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                                                    <div style={{
                                                        height: '100%',
                                                        width: `${syncRate}%`,
                                                        background: syncRate >= 90 ? '#16A34A' : syncRate >= 70 ? '#F59E0B' : '#EF4444',
                                                        borderRadius: 3
                                                    }} />
                                                </div>
                                                <span style={{ fontSize: 12, color: '#666' }}>{syncRate}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Conflicts */}
            {tab === 'conflicts' && (
                <div>
                    {(!data.conflicts || data.conflicts.length === 0) ? (
                        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                            <FiCheck size={40} style={{ marginBottom: 12 }} />
                            <p>Aucun conflit detecte</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>IDs</th>
                                        <th>Commercial</th>
                                        <th>Client</th>
                                        <th>Type</th>
                                        <th>Date</th>
                                        <th>Montants</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.conflicts.map((c: any, i: number) => (
                                        <tr key={i}>
                                            <td>
                                                <span style={{ fontFamily: 'monospace', fontSize: 12 }}>#{c.id1} / #{c.id2}</span>
                                            </td>
                                            <td>{c.commercial}</td>
                                            <td>{c.client || '--'}</td>
                                            <td><span className="badge">{c.type}</span></td>
                                            <td>{c.jour ? new Date(c.jour).toLocaleDateString('fr-FR') : '--'}</td>
                                            <td>
                                                <span>{(c.montant1 || 0).toLocaleString()} F</span>
                                                {' / '}
                                                <span>{(c.montant2 || 0).toLocaleString()} F</span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button className="btn-icon" onClick={() => handleResolveConflict(c.id1, c.id2)} title={`Garder #${c.id1}`}>
                                                        <FiCheck color="#16A34A" size={14} />
                                                        <span style={{ fontSize: 10 }}>#{c.id1}</span>
                                                    </button>
                                                    <button className="btn-icon" onClick={() => handleResolveConflict(c.id2, c.id1)} title={`Garder #${c.id2}`}>
                                                        <FiCheck color="#3B82F6" size={14} />
                                                        <span style={{ fontSize: 10 }}>#{c.id2}</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Connexions */}
            {tab === 'connexions' && (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Commercial</th>
                                <th>Matricule</th>
                                <th>Derniere connexion</th>
                                <th>Statut</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data.connexions || []).map((c: any, i: number) => {
                                const hours = c.heures_depuis || 999;
                                const isOnline = hours < 1;
                                const isRecent = hours < 24;
                                return (
                                    <tr key={i}>
                                        <td><strong>{c.commercial}</strong></td>
                                        <td>{c.matricule || '--'}</td>
                                        <td>
                                            {c.derniere_connexion
                                                ? new Date(c.derniere_connexion).toLocaleString('fr-FR')
                                                : <span style={{ color: '#ccc' }}>Jamais</span>
                                            }
                                        </td>
                                        <td>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                                padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                                                background: isOnline ? '#dcfce7' : isRecent ? '#fef9c3' : '#fee2e2',
                                                color: isOnline ? '#16A34A' : isRecent ? '#ca8a04' : '#EF4444'
                                            }}>
                                                {isOnline ? <FiWifi size={12} /> : isRecent ? <FiClock size={12} /> : <FiWifiOff size={12} />}
                                                {isOnline ? 'En ligne' : isRecent ? `Il y a ${Math.round(hours)}h` : hours < 168 ? `${Math.round(hours / 24)}j` : 'Inactif'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default Synchronisation;
